import { useState, useEffect, useCallback, useRef } from 'react';
import { deviceTabStorage } from '@utils/localStorage';
import { useHasChanged } from './useHasChanged';

interface UseDeviceTabStateProps {
  deviceId: string;
  defaultTab?: number;
  onTabChange?: (tabIndex: number) => void;
}

export const useDeviceTabState = ({
  deviceId,
  defaultTab = 0,
  onTabChange
}: UseDeviceTabStateProps) => {
  const [tabValue, setTabValue] = useState<number>(() => {
    // Initialize with stored value or default
    return deviceTabStorage.getTabIndex(deviceId) || defaultTab;
  });

  // Use ref to track if this is the initial mount
  const isInitialMount = useRef(true);

  // Use ref to debounce storage updates
  const saveTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Persist tab state changes with debouncing
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce storage update to avoid excessive writes
    saveTimeoutRef.current = setTimeout(() => {
      deviceTabStorage.setTabIndex(deviceId, tabValue);
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [deviceId, tabValue]);

  // Handle tab change with callback
  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    onTabChange?.(newValue);
  }, [onTabChange]);

  // Restore the stored tab when the device changes
  if (useHasChanged(deviceId)) {
    setTabValue(deviceTabStorage.getTabIndex(deviceId));
  }

  return {
    tabValue,
    handleTabChange,
    setTabValue,
  };
};

// Hook for lazy loading tab content
export const useLazyTabContent = (currentTab: number, tabIndex: number) => {
  const [hasBeenActive, setHasBeenActive] = useState(currentTab === tabIndex);

  if (currentTab === tabIndex && !hasBeenActive) {
    setHasBeenActive(true);
  }

  return {
    shouldRender: hasBeenActive,
    isActive: currentTab === tabIndex,
  };
};
